/**
 * pdp-color-switch.js
 *
 * Instant PDP colour-swatch switching: swaps only the regions that change
 * between sibling colour products (gallery, title, price, sizes, ATC, ...)
 * instead of doing a full page navigation. Modelled on Oner Active's
 * production implementation - see the approved plan for the full mechanics:
 * /Users/longnguyen/.claude/plans/summary-when-a-customer-flickering-shell.md
 *
 * Fetches, in parallel, per switch:
 *   - /products/<handle>?section_id=<live section id>
 *       The Section Rendering API render of the REAL sections/main-product.liquid
 *       for the sibling product - same section id, same block settings, same
 *       merchant-configured labels and product gates (bundle form, not-for-sale,
 *       athlete template, picker type) as a full page load would produce. Every
 *       region is extracted from this document by the SAME selector used on the
 *       live page, so there is no hand-maintained fragment to drift and no id
 *       rewriting: the render already carries product-form-<section.id> etc.
 *   - /products/<handle>?view=pdp-switch
 *       A tiny layout-none view rendering only snippets/pdp-switch-head-meta.liquid
 *       (title / OG / Twitter / Product JSON-LD), since <head> content lives in
 *       layout/theme.liquid and is not part of any section render.
 *
 * Any failure anywhere in this pipeline - including a region that exists on
 * one side but not the other (bundle sibling, athlete template, picker
 * added/removed) - falls back to a real navigation
 * (window.location.href = '/products/<handle>') rather than leaving the page
 * in a half-swapped state.
 */
(function () {
  'use strict';

  // Guard against the asset somehow being included twice.
  if (window.__mnPdpColorSwitch) return;
  window.__mnPdpColorSwitch = true;

  var SWATCH_RAIL_SELECTOR = '.product-set__wrapper';
  var CACHE_TTL_MS = 5 * 60 * 1000; // renders carry live price/availability
  var CACHE_MAX = 12;

  // Map<handle, { at: number, promise: Promise<{doc, metaDoc}> }>. The
  // in-flight promise is stored immediately (not the resolved value) so a
  // hover-then-click reuses the same request instead of firing a second one.
  var cache = new Map();

  // Concurrency guard: a click while a switch is in flight is queued (not
  // dropped, since preventDefault() already ran) and only the last-queued
  // colour is applied once the in-flight switch settles.
  var switching = false;
  var queuedHandle = null;
  var queuedOpts = null;

  // The product on screen, or the one the latest switch is heading to (set
  // when a switch is asked for, not when it lands, so a Back pressed mid-swap
  // still counts as a change). A popstate or swatch click for this handle has
  // nothing to swap: an in-page link like the skip link (#MainContent) fires
  // popstate on the same product, and re-fetching it re-rendered the page for
  // nothing on apparel PDPs and, on templates without these regions (the
  // Hydration-style supplement pages), fell back to a full reload.
  var shownHandle = null;

  var loadingStyleInjected = false;
  var cachedSectionId = null;

  /* ------------------------------------------------------------------ *
   * Small helpers
   * ------------------------------------------------------------------ */

  // The gallery's <ul id="ProductImageList-{{ section.id }}"> is a stable,
  // always-present anchor for deriving the live section's id - the value the
  // Section Rendering API needs, and the suffix of #price-<id> /
  // #product-form-<id>. Section renders preserve section.id, so this anchor
  // stays valid across swaps.
  function getSectionId() {
    if (cachedSectionId) return cachedSectionId;
    var el = document.querySelector('[id^="ProductImageList-"]');
    if (el) cachedSectionId = el.id.slice('ProductImageList-'.length);
    return cachedSectionId;
  }

  function currentHandleFromLocation() {
    var match = window.location.pathname.match(/\/products\/([^/?#]+)/);
    return match ? match[1] : null;
  }

  function resolveHandleFromAnchor(anchor) {
    if (!anchor || !anchor.href) return null;
    try {
      var url = new URL(anchor.href, window.location.origin);
      var match = url.pathname.match(/\/products\/([^/?#]+)/);
      return match ? match[1] : null;
    } catch (e) {
      return null;
    }
  }

  function checkOk(response) {
    if (!response.ok) throw new Error('pdp-color-switch: HTTP ' + response.status);
    return response;
  }

  function fromFragment(doc, selector, label) {
    var el = doc.querySelector(selector);
    if (!el) throw new Error('pdp-color-switch: fragment missing "' + label + '" (' + selector + ')');
    return el;
  }

  function fromLive(selector, label) {
    var el = document.querySelector(selector);
    if (!el) throw new Error('pdp-color-switch: live DOM missing "' + label + '" (' + selector + ')');
    return el;
  }

  // Both sides render the same section, so live and fresh regions share one
  // selector. innerHTML keeps the live element (and listeners bound directly
  // to it) in place; outerHTML replaces it entirely, re-running custom
  // element constructors on the fresh node via the upgrade on insertion.
  function swapInnerSame(doc, selector, label) {
    var source = fromFragment(doc, selector, label);
    var target = fromLive(selector, label);
    target.innerHTML = source.innerHTML;
    return target;
  }

  function swapOuterSame(doc, selector, label) {
    var source = fromFragment(doc, selector, label);
    var target = fromLive(selector, label);
    target.outerHTML = source.outerHTML;
  }

  /* ------------------------------------------------------------------ *
   * Loading state - one injected <style>, toggled by a body class while a
   * switch is in flight, per the plan (adopted verbatim from Oner).
   * ------------------------------------------------------------------ */

  function ensureLoadingStyle() {
    if (loadingStyleInjected) return;
    loadingStyleInjected = true;
    var style = document.createElement('style');
    style.textContent =
      '.pdp-color-switching .product__image-wrapper,' +
      '.pdp-color-switching .product__image-navigation-wrapper,' +
      '.pdp-color-switching .price,' +
      '.pdp-color-switching .variant-picker,' +
      '.pdp-color-switching .product-form__buy-button{' +
      'opacity:.5;transition:opacity .15s;pointer-events:none;' +
      '}';
    document.head.appendChild(style);
  }

  function toggleLoading(isLoading) {
    ensureLoadingStyle();
    document.body.classList.toggle('pdp-color-switching', isLoading);
  }

  /* ------------------------------------------------------------------ *
   * Fetch + cache
   * ------------------------------------------------------------------ */

  function fetchRenderPair(handle) {
    var sectionId = getSectionId();
    if (!sectionId) {
      return Promise.reject(new Error('pdp-color-switch: no live section id anchor'));
    }
    var cleanUrl = '/products/' + handle;
    return Promise.all([
      fetch(cleanUrl + '?section_id=' + encodeURIComponent(sectionId))
        .then(checkOk)
        .then(function (r) { return r.text(); }),
      fetch(cleanUrl + '?view=pdp-switch')
        .then(checkOk)
        .then(function (r) { return r.text(); }),
    ]).then(function (results) {
      if (!results[0] || !results[1]) throw new Error('pdp-color-switch: empty render for ' + handle);
      var doc = new DOMParser().parseFromString(results[0], 'text/html');
      var metaDoc = new DOMParser().parseFromString(results[1], 'text/html');
      // Sanity: the sibling must render this same section (a product on a
      // different template returns 404 -> checkOk, but guard soft-200s too).
      fromFragment(doc, '#product-header header', 'title');
      return { doc: doc, metaDoc: metaDoc };
    });
  }

  function pruneCache() {
    var now = Date.now();
    cache.forEach(function (entry, key) {
      if (now - entry.at > CACHE_TTL_MS) cache.delete(key);
    });
    if (cache.size <= CACHE_MAX) return;
    var byAge = Array.from(cache.entries()).sort(function (a, b) { return a[1].at - b[1].at; });
    while (cache.size > CACHE_MAX && byAge.length) {
      cache.delete(byAge.shift()[0]);
    }
  }

  function getCachedFetch(handle) {
    pruneCache();
    var existing = cache.get(handle);
    if (existing) return existing;

    var promise = fetchRenderPair(handle).catch(function (err) {
      // Never cache a failure - a transient error on a hover-prefetch the
      // shopper never saw must not poison this handle for the whole TTL.
      cache.delete(handle);
      throw err;
    });
    var entry = { at: Date.now(), promise: promise };
    cache.set(handle, entry);
    return entry;
  }

  /* ------------------------------------------------------------------ *
   * Region swaps - every source comes from the section render, so block
   * settings (labels, picker type) and product gates (bundle, not-for-sale,
   * athlete) are exactly what a full page load of the sibling would show.
   * A region present on one side but not the other means the sibling's real
   * page differs structurally from this one - throw, so performSwitch falls
   * back to a genuine navigation instead of leaving a half-swapped page.
   * ------------------------------------------------------------------ */

  function swapTitle(doc) {
    swapOuterSame(doc, '#product-header header', 'title');
  }

  function swapSwatchRail(doc) {
    var live = document.querySelector(SWATCH_RAIL_SELECTOR);
    var fresh = doc.querySelector(SWATCH_RAIL_SELECTOR);
    if (live && fresh) {
      live.outerHTML = fresh.outerHTML;
    } else {
      // e.g. an athlete-tagged sibling, whose real template hides the rail.
      throw new Error('pdp-color-switch: swatch-rail mismatch');
    }
  }

  function swapGallery(doc) {
    swapInnerSame(doc, '.product-gallery-wrap', 'gallery');
  }

  // The whole #price-<id> container, not just the inner .price: the section
  // also renders the message: tag span and block custom_liquid in there, and
  // show_badges honours the sibling's own not-for-sale/soon state.
  //
  // `price` is an ordinary section block, so a template can legitimately render
  // no #price-<id> at all - the apparel PDPs show the price inside the ATC
  // button instead (.ig-atc-price-gate, main-product-add-to-cart.liquid). Both
  // sides render the same section config, so absent on BOTH sides is expected
  // and there is no price region to go stale: the button that does carry the
  // price is replaced by swapBuyButtons. Treated as a hard requirement, this
  // threw on every switch on those templates and sent the shopper through a
  // full page navigation - the white flash the swap exists to avoid. Hard-fail
  // is kept for a genuine mismatch, where a stale price really would be on
  // screen.
  function swapPrice(doc) {
    var selector = '#price-' + getSectionId();
    var live = document.querySelector(selector);
    var fresh = doc.querySelector(selector);

    if (live && fresh) {
      live.innerHTML = fresh.innerHTML;
    } else if (live || fresh) {
      throw new Error('pdp-color-switch: price-region mismatch (' + selector + ')');
    }
  }

  // outerHTML swap of the whole .variant-picker wrapper: the fresh
  // <variant-radios> upgrades on insertion (VariantSelects constructor
  // rebinds its change listener) and carries the sibling's own data-validate,
  // data-size-option-position, data-section, data-url, data-product-type and
  // variants JSON - nothing stays pinned to the previous product.
  // Scoped to #product-header: sections/supplement-info.liquid renders its
  // own .variant-picker, so the bare class is not unique page-wide.
  var MAIN_PICKER_SELECTOR = '#product-header .variant-picker';

  function swapVariantPicker(doc) {
    var live = document.querySelector(MAIN_PICKER_SELECTOR);
    var fresh = doc.querySelector(MAIN_PICKER_SELECTOR);

    if (live && fresh) {
      live.outerHTML = fresh.outerHTML;
    } else if (live && !fresh) {
      // Sibling has only a default variant - its real page renders no picker.
      live.parentNode.removeChild(live);
    } else if (!live && fresh) {
      // No live anchor to place the sibling's picker at - a silent skip here
      // would leave a multi-size product with no size selector, quietly
      // adding a variant the shopper never chose. Fall back to navigation.
      throw new Error('pdp-color-switch: sibling needs a variant picker this page lacks');
    }
    // Neither side has a picker: both sell a single default variant - fine.

    return document.querySelector(MAIN_PICKER_SELECTOR + ' variant-radios');
  }

  function swapStickyVariantPicker(doc) {
    var live = document.querySelector('.sticky_variants_wrapper');
    var fresh = doc.querySelector('.sticky_variants_wrapper');
    if (live && fresh) {
      live.outerHTML = fresh.outerHTML;
    } else if (live && !fresh) {
      live.parentNode.removeChild(live);
    } else if (!live && fresh) {
      throw new Error('pdp-color-switch: sticky-picker mismatch');
    }
  }

  function swapBuyButtons(doc) {
    var selector = '#product-header .product-form__button-wrapper product-form.product-form__buy-button';
    var live = document.querySelector(selector);
    var fresh = doc.querySelector(selector);
    if (live && fresh) {
      live.outerHTML = fresh.outerHTML;
    } else {
      // The sibling's real page renders something else here - a bundle form
      // (custom.bundle_items) or nothing at all (price 0 / not purchasable).
      // Swapping in a plain ATC would sell it unconfigured; navigate instead.
      throw new Error('pdp-color-switch: buy-button mismatch (bundle / zero-price sibling)');
    }
  }

  // back_in_stock is an ordinary section block - merchants can disable or
  // remove it per template. Both sides render the same section config, so
  // missing on both sides is expected; the swap stays soft.
  function swapBackInStock(doc) {
    var source = doc.querySelector('.pdp-back-in-stock');
    var target = document.querySelector('.pdp-back-in-stock');
    if (source && target) target.innerHTML = source.innerHTML;
  }

  // "Complete the Look" (snippets/main-product-related-product.liquid) is
  // driven by the PRODUCT's own features.related_products metafield, and every
  // sibling colour points at its own colour-matched partner (Travertine
  // bralette -> Travertine shorts). Without this swap the shopper keeps seeing
  // the LANDING colour's match after switching. The snippet renders
  // .product__related-product-region unconditionally, so one selector is valid
  // on both sides whether the sibling has several related products, one, or
  // none. Scoped to #product-header because sections/main-supplement.liquid
  // renders its own related-products markup lower down the page.
  // Soft: a section config without a related_product block renders no region
  // on either side and has nothing to do.
  var RELATED_REGION_SELECTOR = '#product-header .product__related-product-region';

  function swapRelatedProducts(doc) {
    var fresh = doc.querySelector(RELATED_REGION_SELECTOR);
    var live = document.querySelector(RELATED_REGION_SELECTOR);
    if (!fresh || !live) return;
    // Splide binds window resize/scroll listeners that would outlive the
    // slides about to be discarded - tear the old carousel down first, since a
    // shopper can switch colours many times on one page view.
    destroyRelatedProductsCarousel(live);
    live.innerHTML = fresh.innerHTML;
  }

  function destroyRelatedProductsCarousel(root) {
    try {
      if (typeof window.mnDestroyRelatedProductsCarousels === 'function') {
        window.mnDestroyRelatedProductsCarousels(root);
      }
    } catch (e) {
      /* teardown is best-effort - never block the swap */
    }
  }

  // Product-driven blocks in the info column (description, Model Details,
  // About the fabric, Care instructions) render from the PRODUCT's own
  // description and custom.* metafields, and each sibling colour can carry its
  // own: a different model, fabric or care note. Without this swap the shopper
  // keeps seeing the landing colour's copy.
  // sections/main-product.liquid brackets each such block's output in
  //   <!--mn-swap-region:start:<block.id>--> ... <!--mn-swap-region:end:<block.id>-->
  // whether or not the block has anything to show, so both sides have a stable
  // anchor. Comments, because a wrapper element would break the
  // `.accordion + .accordion` border rule (component-accordion.css). The list
  // of bracketed block types lives in the section, so swapping another
  // product-driven block needs no change here.
  var SWAP_REGION_MARKER = /^mn-swap-region:(start|end):(\S+)$/;

  // Map<block id, { start, end }> of every complete marker pair in #product-header.
  function collectSwapRegions(root) {
    var regions = new Map();
    var header = root.querySelector('#product-header');
    if (!header) return regions;
    var walker = (header.ownerDocument || document).createTreeWalker(header, NodeFilter.SHOW_COMMENT);
    while (walker.nextNode()) {
      var match = SWAP_REGION_MARKER.exec(walker.currentNode.data.trim());
      if (!match) continue;
      var region = regions.get(match[2]) || {};
      region[match[1]] = walker.currentNode;
      regions.set(match[2], region);
    }
    regions.forEach(function (region, id) {
      if (!region.start || !region.end || region.start.parentNode !== region.end.parentNode) {
        regions.delete(id);
      }
    });
    return regions;
  }

  function regionNodes(region) {
    var nodes = [];
    for (var node = region.start.nextSibling; node && node !== region.end; node = node.nextSibling) {
      nodes.push(node);
    }
    return nodes;
  }

  function detailsIn(nodes) {
    var found = [];
    nodes.forEach(function (node) {
      if (node.nodeType === Node.ELEMENT_NODE) {
        Array.prototype.push.apply(found, node.querySelectorAll('details'));
      }
    });
    return found;
  }

  function swapProductRegions(doc) {
    var fresh = collectSwapRegions(doc);
    collectSwapRegions(document).forEach(function (live, id) {
      var liveNodes = regionNodes(live);
      // Accordions the shopper had open stay open, matched by position.
      var openStates = detailsIn(liveNodes).map(function (details) { return details.open; });

      // A region the sibling's render lacks means its real page does not show
      // this block (it is on another template), so the region is emptied
      // rather than left showing the landing colour's content.
      var fragment = null;
      var freshRegion = fresh.get(id);
      if (freshRegion) {
        // Clone into an inert <template> rather than moving the cached doc's
        // nodes (later switches reuse it). Open state is set before insertion,
        // so DetailsAccordion's constructor - which runs on insertion and reads
        // `open` once for aria-expanded - sees the final state.
        fragment = document.createElement('template').content;
        regionNodes(freshRegion).forEach(function (node) {
          fragment.appendChild(node.cloneNode(true));
        });
        detailsIn(Array.prototype.slice.call(fragment.childNodes)).forEach(function (details, i) {
          if (i < openStates.length) details.open = openStates[i];
        });
      }

      liveNodes.forEach(function (node) {
        node.parentNode.removeChild(node);
      });
      if (fragment) live.end.parentNode.insertBefore(fragment, live.end);
    });
    // A region only the sibling renders has no live anchor to go in. Both sides
    // render the same marker pairs for the same template, so that only happens
    // when the sibling is on another template, and it is skipped.
  }

  // #ProductData ({{ product | json }}) is what klaviyo-back-in-stock.js
  // reads variants from - swap it so post-switch signups subscribe to the
  // product actually on screen, and return the parsed product for the
  // dataLayer push / form data-productid update.
  function swapProductData(doc) {
    var fresh = doc.querySelector('#ProductData');
    var live = document.getElementById('ProductData');
    if (fresh && live) live.textContent = fresh.textContent;
    try {
      return JSON.parse((live || fresh).textContent);
    } catch (e) {
      return null;
    }
  }

  // The zoom lightbox (snippets/product-zoom-lightbox.liquid) sits outside
  // every other swap region: a <product-modal id="ProductModal-<section.id>">
  // holding a Swiper whose slides are server-rendered from product.media.
  // Without this swap the modal still opens after a colour switch (the fresh
  // gallery openers target the same id) but shows the PREVIOUS colour's
  // slides. Replace the whole element with the section render's copy - fresh
  // slides, fresh ModalDialog bindings via the custom-element upgrade. Soft:
  // templates without the lightbox have nothing to do.
  function swapZoomLightbox(doc) {
    var id = 'ProductModal-' + getSectionId();
    var live = document.getElementById(id);
    var fresh = doc.getElementById(id);
    if (live && fresh) live.outerHTML = fresh.outerHTML;
  }

  /* ------------------------------------------------------------------ *
   * Re-init sequence (after the straight swaps above)
   * ------------------------------------------------------------------ */

  // product-zoom-lightbox.liquid's section-inline script declares a global
  // initializeSwiper() guarded by `if (swiperEl.swiper) return`, so calling
  // it again is safe and does exactly what a fresh page load does: builds a
  // new Swiper on the just-swapped modal and rebinds the gallery buttons'
  // data-image-id -> openedTargetMediaId click listeners (the old bindings
  // died with the swapped gallery/modal nodes).
  function reinitZoomLightbox() {
    try {
      if (typeof window.initializeSwiper === 'function') window.initializeSwiper();
    } catch (e) {
      /* zoom re-init must never break the swap */
    }
  }

  // The snippet's carousel mount runs off DOMContentLoaded and <script> tags
  // injected via innerHTML never execute, so the freshly swapped slider needs
  // mounting by hand. mnInitRelatedProductsCarousels() is idempotent (it skips
  // anything already initialised) and a no-op for a single related product,
  // which renders no `splide` class at all.
  function reinitRelatedProductsCarousel() {
    try {
      if (typeof window.mnInitRelatedProductsCarousels === 'function') {
        window.mnInitRelatedProductsCarousels(document.querySelector(RELATED_REGION_SELECTOR));
      }
    } catch (e) {
      /* a failed carousel mount must never break the swap */
    }
  }

  // The gallery swap brings in the sibling colour's <video> slides, which ship
  // preload="none" with no autoplay attribute: pdp-gallery-video.js decides when
  // they buffer and play, and it only binds on load and shopify:section:load -
  // neither of which fires here - so without this call the new colour's video
  // sits frozen on its poster. bindVideos() is idempotent (bound videos carry
  // data-gallery-video-bound) and also releases the outgoing colour's videos
  // from the observers.
  function reinitGalleryVideo() {
    try {
      if (typeof window.mnBindPdpGalleryVideos === 'function') window.mnBindPdpGalleryVideos();
    } catch (e) {
      /* a failed video re-bind must never break the swap */
    }
  }

  // {optionName: checkedValue} for every option fieldset (LENGTH, SIZE, ...).
  // Radios are named after their Liquid option (name="{{ option.name }}"),
  // and sibling colours render the same option names, so the name is a
  // stable key across the swap.
  function getCheckedOptionValues(host) {
    var out = {};
    if (!host) return out;
    var checked = host.querySelectorAll('fieldset.product-form__input input[type="radio"]:checked');
    Array.prototype.forEach.call(checked, function (r) {
      if (r.name) out[r.name] = r.value;
    });
    return out;
  }

  // Replays a real interaction (check + a genuine bubbling `change` Event)
  // rather than hand-invoking VariantRadios' private update methods, so
  // every dependent side effect (ATC state, price re-render, the
  // `variantChange` CustomEvent assets/pdp-group-swatch-availability.js
  // listens for, ...) fires exactly as it would for a real click. Only ever
  // runs on the main picker - never the sticky one, whose change handler
  // auto-submits the cart form (global.js sticky_variant_radios branch).
  //
  // Carries every option the shopper had selected (LENGTH on combined-listing
  // parents like Aura Straight Leg, SIZE, ...). A previous value that the
  // sibling colour doesn't offer (or has sold out) leaves that fieldset on
  // the sibling's server-rendered default. For SIZE that default is unpicked,
  // so the buy button reads "Select size" instead of adding a size the
  // shopper never chose (it used to fall back to the first available size,
  // e.g. XXS). All radios are set first and ONE
  // change event is dispatched at the end, so VariantRadios recomputes the
  // variant once from the final combination instead of once per option.
  function carryOverOptions(mainHost, previousValues) {
    if (!mainHost) return;
    var fieldsets = mainHost.querySelectorAll('fieldset.product-form__input');
    if (!fieldsets.length) return;

    var dispatchTarget = null;
    Array.prototype.forEach.call(fieldsets, function (fieldset) {
      var radios = Array.prototype.slice.call(fieldset.querySelectorAll('input[type="radio"]'));
      if (!radios.length) return;

      var previousValue = previousValues[radios[0].name];
      var target = null;
      if (previousValue) {
        target = radios.filter(function (r) { return r.value === previousValue; })[0];
        if (target && target.classList.contains('disabled')) target = null;
      }
      if (!target) return; // keep the sibling's default
      if (target.checked) {
        dispatchTarget = dispatchTarget || target;
        return;
      }

      radios.forEach(function (r) { r.checked = r === target; });
      dispatchTarget = target;
    });

    if (dispatchTarget) dispatchTarget.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function updateFormProductId(product) {
    if (!product) return;
    var form = document.getElementById('product-form-' + getSectionId());
    if (form) form.setAttribute('data-productid', product.id);
  }

  // assets/gallery-scroll-indicator.js is a bare IIFE with no exposed re-init
  // hook (and is out of scope for this change to edit) - its scroll/resize
  // listeners stay bound to the never-replaced .product-gallery-wrap, so
  // those keep working, but its up/down buttons are fresh nodes after the
  // gallery swap and need their click handlers re-attached here. Its
  // proportional scroll-thumb sizing still reads stale (pre-swap) element
  // references internally and will not track the new gallery until a full
  // reload - a known, minor, accepted visual gap flagged for QA.
  function reinitGalleryScrollIndicator() {
    var galleryWrap = document.querySelector('.product-gallery-wrap');
    var upBtn = document.getElementById('gallery-scroll-up');
    var downBtn = document.getElementById('gallery-scroll-down');
    if (!galleryWrap || !upBtn || !downBtn) return;
    upBtn.addEventListener('click', function () {
      galleryWrap.scrollBy({ top: -300, behavior: 'smooth' });
    });
    downBtn.addEventListener('click', function () {
      galleryWrap.scrollBy({ top: 300, behavior: 'smooth' });
    });
  }

  // assets/klaviyo-back-in-stock.js's BackInStockKlaviyo captures
  // #notify-when-available-btn once in its constructor; that node is a fresh
  // element after the buy-buttons swap, so its click binding needs
  // re-attaching by hand, and its variant-input listeners need re-binding to
  // the fresh SIZE radios. Its cached productData is cleared so
  // loadProductData() re-parses the freshly swapped #ProductData blob and
  // signups subscribe to the product on screen, not the landing product.
  // The separate `.bis-link` "Notify when available" button (the
  // back_in_stock block, out-of-stock colours) needs no call at all:
  // CollectionBackInStock wires new `.bis-link` nodes automatically via a
  // MutationObserver on document.body, so it self-heals across this swap.
  function reinitBackInStock() {
    var bis = window.backInStockKlaviyoInstance;
    if (!bis) return; // klaviyo-back-in-stock.js not loaded on this template
    bis.productData = null;
    var freshBtn = document.getElementById('notify-when-available-btn');
    if (freshBtn && freshBtn !== bis.notifyBtn) {
      bis.notifyBtn = freshBtn;
      freshBtn.addEventListener('click', bis.handleNotifyClick.bind(bis));
    }
    if (typeof bis.setupVariantListener === 'function') bis.setupVariantListener();
  }

  // Judge.me is integrated via a metafield-driven star rating
  // (snippets/product-rating.liquid) plus jdgm-widget markup elsewhere in the
  // theme (snippets/card-product-judgeme-review.liquid) - no theme JS call
  // exists for it today, but batchRelayout() is Judge.me's own documented
  // "re-scan the DOM" API, so calling it defensively here is the correct,
  // low-risk move for whatever the app's own script may have hydrated.
  function reinitJudgeMe() {
    try {
      if (window.jdgm && typeof window.jdgm.batchRelayout === 'function') {
        window.jdgm.batchRelayout();
      }
    } catch (e) {
      /* Judge.me's real API is unverified here - never let this break the swap */
    }
  }

  function metaKey(el) {
    if (el.hasAttribute('property')) return 'property|' + el.getAttribute('property');
    if (el.hasAttribute('name')) return 'name|' + el.getAttribute('name');
    return null;
  }

  // Groups by "<attr>|<value>" preserving document order, so duplicate meta
  // names (e.g. multiple google-site-verification tags) update by position
  // instead of every key collapsing onto the first live match.
  function groupMetaByKey(root) {
    var groups = new Map();
    root.querySelectorAll('meta[property], meta[name]').forEach(function (el) {
      var key = metaKey(el);
      if (!key) return;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(el);
    });
    return groups;
  }

  function patchMetaTags(fragmentRoot) {
    var liveGroups = groupMetaByKey(document.head);
    var freshGroups = groupMetaByKey(fragmentRoot);

    freshGroups.forEach(function (freshEls, key) {
      var liveEls = liveGroups.get(key) || [];
      freshEls.forEach(function (freshEl, index) {
        var liveEl = liveEls[index];
        if (liveEl) {
          liveEl.setAttribute('content', freshEl.getAttribute('content'));
        } else {
          document.head.appendChild(freshEl.cloneNode(true));
        }
      });
    });
  }

  function findProductJsonLd(root) {
    var scripts = root.querySelectorAll('script[type="application/ld+json"]');
    for (var i = 0; i < scripts.length; i++) {
      try {
        var parsed = JSON.parse(scripts[i].textContent);
        if (parsed && parsed['@type'] === 'Product') return scripts[i];
      } catch (e) {
        /* malformed or foreign schema block (WebSite/Organization/breadcrumb) - skip */
      }
    }
    return null;
  }

  function patchHeadMeta(metaDoc, handle) {
    var metaRoot = fromFragment(metaDoc, '#pdp-switch-head-meta', 'head meta');

    var titleEl = metaRoot.querySelector('#pdp-switch-meta-title');
    if (titleEl) document.title = titleEl.textContent.trim();

    var canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute('href', window.location.origin + '/products/' + handle);

    patchMetaTags(metaRoot);

    // Only the Product-typed script is touched - WebSite/Organization/
    // breadcrumb schema (snippets/mn-seo-schema.liquid) aren't colour-dependent.
    var freshLd = findProductJsonLd(metaRoot);
    var liveLd = findProductJsonLd(document);
    if (freshLd && liveLd) liveLd.textContent = freshLd.textContent;
  }

  /* ------------------------------------------------------------------ *
   * Orchestration
   * ------------------------------------------------------------------ */

  function applySwap(doc, metaDoc, handle) {
    var mainHostBefore = document.querySelector(MAIN_PICKER_SELECTOR + ' variant-radios');
    var previousOptions = getCheckedOptionValues(mainHostBefore);

    swapTitle(doc);
    swapSwatchRail(doc);
    swapGallery(doc);
    swapPrice(doc);
    var mainHost = swapVariantPicker(doc);
    swapStickyVariantPicker(doc);
    swapBuyButtons(doc);
    swapBackInStock(doc);
    swapRelatedProducts(doc);
    swapProductRegions(doc);
    swapZoomLightbox(doc);
    var product = swapProductData(doc);

    reinitGalleryScrollIndicator();
    reinitGalleryVideo();
    reinitZoomLightbox();
    reinitRelatedProductsCarousel();
    carryOverOptions(mainHost, previousOptions);
    updateFormProductId(product);
    reinitBackInStock();
    reinitJudgeMe();
    patchHeadMeta(metaDoc, handle);

    // Intelligems (.ig-* class-based A/B experiments): no theme-side re-init
    // call exists or is planned - flagged in the plan as an unmanaged risk to
    // verify in QA, not something safe to guess an API for here.

    return product;
  }

  function performSwitch(handle, opts) {
    toggleLoading(true);
    return getCachedFetch(handle).promise
      .then(function (result) {
        var product = applySwap(result.doc, result.metaDoc, handle);

        if (opts.pushState !== false) {
          history.pushState({ handle: handle }, '', '/products/' + handle);
        }

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'virtualPageview',
          page: '/products/' + handle,
          productId: product && product.id,
          productTitle: product && product.title,
        });
      })
      .catch(function (err) {
        // Any failure anywhere above (fetch, missing region, re-init) - hard
        // fallback to a real navigation rather than a half-swapped page.
        console.error('[pdp-color-switch] falling back to full navigation:', err);
        cache.delete(handle);
        window.location.href = '/products/' + handle;
      })
      .finally(function () {
        toggleLoading(false);
      });
  }

  function switchColor(handle, opts) {
    opts = opts || {};
    shownHandle = handle;
    if (switching) {
      queuedHandle = handle;
      queuedOpts = opts;
      return;
    }
    switching = true;
    performSwitch(handle, opts).finally(function () {
      switching = false;
      if (queuedHandle) {
        var nextHandle = queuedHandle;
        var nextOpts = queuedOpts;
        queuedHandle = null;
        queuedOpts = null;
        switchColor(nextHandle, nextOpts);
      }
    });
  }

  /* ------------------------------------------------------------------ *
   * Prefetch + click interception, scoped to the swatch rail wrapper
   * (confirmed via grep to have no second instance elsewhere in the theme,
   * so no exclusion list is needed).
   * ------------------------------------------------------------------ */

  function swatchAnchorFromEvent(event) {
    return event.target.closest ? event.target.closest(SWATCH_RAIL_SELECTOR + ' a') : null;
  }

  function prefetch(anchor) {
    var handle = resolveHandleFromAnchor(anchor);
    if (handle) getCachedFetch(handle);
  }

  // Previews the hovered/focused swatch's name in the "Colour: <name>" /
  // "Core: <name>" label above its own list, reverting on pointerout/
  // focusout. The label immediately preceding a swatch <ul> is always the
  // one that list belongs to (see the 'set' block markup) - pointerout only
  // fires on a true exit of the anchor's box, not on moving onto its
  // .pdp-group-swatch__sphere child, so no extra flicker guard is needed.
  function labelForAnchor(anchor) {
    var list = anchor.closest('.pdp-group-swatches');
    var label = list && list.previousElementSibling;
    return label && label.classList.contains('product-set__title') ? label : null;
  }

  function subtitleElFor(label) {
    var span = label.querySelector('.product-set__subtitle');
    if (!span) {
      span = document.createElement('span');
      span.className = 'product-set__subtitle';
      label.appendChild(span);
    }
    return span;
  }

  function previewSwatchName(anchor) {
    var name = anchor.dataset.swatchName;
    var label = name && labelForAnchor(anchor);
    if (!label) return;
    var span = subtitleElFor(label);
    if (span.dataset.defaultText === undefined) span.dataset.defaultText = span.textContent;
    span.textContent = name;
  }

  function restoreSwatchName(anchor) {
    var label = labelForAnchor(anchor);
    var span = label && label.querySelector('.product-set__subtitle');
    if (span && span.dataset.defaultText !== undefined) span.textContent = span.dataset.defaultText;
  }

  document.addEventListener('pointerover', function (event) {
    var anchor = swatchAnchorFromEvent(event);
    if (anchor) {
      prefetch(anchor);
      previewSwatchName(anchor);
    }
  });
  document.addEventListener('pointerout', function (event) {
    var anchor = swatchAnchorFromEvent(event);
    if (anchor) restoreSwatchName(anchor);
  });
  document.addEventListener('focusin', function (event) {
    var anchor = swatchAnchorFromEvent(event);
    if (anchor) {
      prefetch(anchor);
      previewSwatchName(anchor);
    }
  });
  document.addEventListener('focusout', function (event) {
    var anchor = swatchAnchorFromEvent(event);
    if (anchor) restoreSwatchName(anchor);
  });
  document.addEventListener('touchstart', function (event) {
    var anchor = swatchAnchorFromEvent(event);
    if (anchor) prefetch(anchor);
  }, { passive: true });

  document.addEventListener('click', function (event) {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    var anchor = swatchAnchorFromEvent(event);
    if (!anchor) return;
    var handle = resolveHandleFromAnchor(anchor);
    if (!handle) return;
    event.preventDefault();
    // The current colour's own swatch (aria-current="page"): nothing to do.
    if (handle === shownHandle) return;
    switchColor(handle);
  });

  /* ------------------------------------------------------------------ *
   * History + init
   * ------------------------------------------------------------------ */

  function init() {
    // This script loads on every page (see layout/theme.liquid), but only a
    // product page ever has a handle to seed - skip touching history.state
    // anywhere else so unrelated pages' own history state is left alone.
    var handle = currentHandleFromLocation();
    if (!handle) return;
    shownHandle = handle;

    // Seeds state.handle so the very first popstate a shopper triggers (land
    // on the PDP, click one swatch, hit Back) always has something to read.
    history.replaceState({ handle: handle }, '', window.location.href);

    window.addEventListener('popstate', function (event) {
      var targetHandle = (event.state && event.state.handle) || currentHandleFromLocation();
      if (!targetHandle || targetHandle === shownHandle) return;
      switchColor(targetHandle, { pushState: false });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
